import {useMemo, useState} from 'react'
import {Stack} from '@/components/shared/Stack'
import {useQuery} from '@tanstack/react-query'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useDialog} from '@/hooks/useDialog'
import {Plus} from 'lucide-react'
import {ClickableTableRow} from '@/components/shared/ClickableTableRow'
import {TextLink} from '@/components/shared/TextLink'
import {usePageTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {FormGrid} from '@/components/shared/FormGrid'
import {Icon} from '@/components/shared/Icon'
import {routes} from '@/lib/routes'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {Button} from '@/components/ui/button'
import {Badge} from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {DialogForm} from '@/components/shared/DialogForm'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Pagination} from '@/components/ui/pagination'
import {useServerPagination} from '@/hooks/use-server-pagination'
import {RoleControllerRolesEndpoint} from '@/types/endpoint-contracts'
import {useAuth} from '@/contexts/auth-context'
import {permissions} from '@/lib/permissions'
import {RoleCreateRequestFields} from '@/types/roles'
import type {RoleCreateRequest} from '@/types/roles'
import {PageLayout} from '@/components/shared/PageLayout'
import {CodeText} from '@/components/shared/CodeText'
import {StatusText} from '@/components/shared/StatusText'

export function Roles() {
    const i18n = useI18n()
    const services = useServices()
    usePageTitle(i18n.roles.pageTitle)
    const {hasPermission} = useAuth()
    const createDialog = useDialog()
    const [formData, setFormData] = useState({key: '', name: '', description: ''})

    const canCreate = hasPermission(permissions.roles.create)


    const ROLES_KEY = 'role-list' as const


    const {
        page, pageSize, queryString,
        setPage, setPageSize, pageSizeOptions,
        searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: RoleControllerRolesEndpoint,
        defaultSortBy: RoleControllerRolesEndpoint.sort.NAME,
    })

    const hasActiveFilters = useMemo(() => {
        return !!searchAndFilters.q
            || Object.keys(searchAndFilters.filters).length > 0
            || Object.keys(searchAndFilters.dynamicAttributes).length > 0
    }, [searchAndFilters])

    // Fetch roles with server-side pagination
    const {data: rolesData, isLoading, error} = useQuery({
        queryKey: [ROLES_KEY, queryString, page, pageSize],
        queryFn: () => services.roles.list(Object.fromEntries(new URLSearchParams(queryString))),
    })

    const roles = rolesData?.content || []
    const totalItems = rolesData?.totalElements || 0
    const totalPages = rolesData?.totalPages || 0

    // Create role mutation
    const createMutation = useEntityMutation({
        mutationFn: (data: RoleCreateRequest) => services.roles.create(data),
        invalidate: [ROLES_KEY],
        successMessage: i18n.roles.roleCreated,
        closeDialog: () => createDialog.close(),
        onSuccess: () => {
            resetForm()
        },
    })

    const resetForm = () => {
        setFormData({key: '', name: '', description: ''})
    }

    const handleCreate = () => {
        createMutation.mutate({
            key: formData.key,
            name: formData.name,
            description: formData.description || undefined,
        })
    }

    const isValidKey = /^[a-z0-9]+(-[a-z0-9]+)*$/.test(formData.key) && formData.key.length <= 64


    return (
        <>
            <PageLayout
                variant="list"
                icon={CONCEPT_ICON.adminRole}
                loading={isLoading}
                error={error}
                title={i18n.roles.allRoles}
                count={totalItems}
                description={`${i18n.roles.count({count: totalItems})} ${hasActiveFilters ? i18n.common.matchingFilter : ''}`}
                data-testid="roles-list"
                actions={canCreate ? (
                    <Button onClick={createDialog.open} data-testid="create-role-button">
                        <Icon icon={Plus} className="mr-2"/>
                        {i18n.roles.createRole}
                    </Button>
                ) : undefined}
                filters={
                    <UnifiedSearchBar
                        contract={RoleControllerRolesEndpoint}
                        value={searchAndFilters}
                        onChange={setSearchAndFilters}
                        placeholder={i18n.roles.filterPlaceholder}
                    />
                }
                pagination={totalItems > 0 ? (
                    <Pagination
                        page={page}
                        pageSize={pageSize}
                        totalItems={totalItems}
                        totalPages={totalPages}
                        onPageChange={setPage}
                        onPageSizeChange={setPageSize}
                        pageSizeOptions={pageSizeOptions}
                    />
                ) : undefined}
            >
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead data-testid="col-key">{i18n.roles.keyColumn}</TableHead>
                            <TableHead data-testid="col-name">{i18n.common.name}</TableHead>
                            <TableHead data-testid="col-description">{i18n.common.description}</TableHead>
                            <TableHead data-testid="col-type">{i18n.common.type}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {roles.map((role) => (
                            <ClickableTableRow key={role.id} data-testid={`role-row-${role.id}`} to={routes.admin.roles.detail(role.id)}>
                                <TableCell data-testid={`role-key-${role.id}`}>
                                    <CodeText>{role.key}</CodeText>
                                </TableCell>
                                <TableCell className="font-medium">
                                    <TextLink
                                        to={routes.admin.roles.detail(role.id)}
                                        testId={`link-role-detail-${role.id}`}
                                        stopPropagation
                                    >
                                        {role.name}
                                    </TextLink>
                                </TableCell>
                                <TableCell className="text-muted-foreground" data-testid={`role-description-${role.id}`}>
                                    {role.description || '\u2014'}
                                </TableCell>
                                <TableCell>
                                    <Badge
                                        variant="outline" data-testid={`role-type-badge-${role.id}`}>{role.systemRole ? i18n.roles.system : i18n.roles.custom}</Badge>
                                </TableCell>
                            </ClickableTableRow>
                        ))}
                        {roles.length === 0 && (
                            <TableEmptyState
                                colSpan={4}
                                hasFilters={hasActiveFilters}
                                matchMessage={i18n.roles.noRolesMatch}
                                emptyMessage={i18n.roles.noRolesFound}
                            />
                        )}
                    </TableBody>
                </Table>
            </PageLayout>

            {/* Create Dialog */}
            <Dialog open={createDialog.isOpen} onOpenChange={createDialog.bind.onOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle data-testid="create-role-dialog-title">{i18n.roles.createRole}</DialogTitle>
                        <DialogDescription>
                            {i18n.roles.createDescription}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogForm onSubmit={(e) => { e.preventDefault(); handleCreate() }}>
                        <Stack className="py-4">
                            <FormGrid>
                                <Label htmlFor={RoleCreateRequestFields.key}>{i18n.roles.keyLabel}</Label>
                                <Input
                                    id={RoleCreateRequestFields.key}
                                    value={formData.key}
                                    onChange={(e) => setFormData({...formData, key: e.target.value})}
                                    placeholder={i18n.roles.keyPlaceholder}
                                    data-testid="role-key-input"
                                    autoComplete="off"
                                    spellCheck={false}
                                />
                                {formData.key && !isValidKey && (
                                    <StatusText tone="destructive" size="sm" data-testid="role-key-error">
                                        {i18n.roles.keyValidationError}
                                    </StatusText>
                                )}
                            </FormGrid>
                            <FormGrid>
                                <Label htmlFor={RoleCreateRequestFields.name}>{i18n.common.name}</Label>
                                <Input
                                    id={RoleCreateRequestFields.name}
                                    value={formData.name}
                                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                                    placeholder={i18n.roles.namePlaceholder}
                                    data-testid="role-name-input"
                                />
                            </FormGrid>
                            <FormGrid>
                                <Label htmlFor={RoleCreateRequestFields.description}>{i18n.common.description}</Label>
                                <Input
                                    id={RoleCreateRequestFields.description}
                                    value={formData.description}
                                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                                    placeholder={i18n.roles.descriptionPlaceholder}
                                    data-testid="role-description-input"
                                />
                            </FormGrid>
                        </Stack>
                        <SaveCancelFooter
                            testIdPrefix="create"
                            onCancel={createDialog.close}
                            saving={createMutation.isPending}
                            disabled={!formData.name || !isValidKey}
                            saveLabel={i18n.common.create}
                            savingLabel={i18n.common.creating}
                        />
                    </DialogForm>
                </DialogContent>
            </Dialog>
        </>
    )
}
